import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Select,
  MenuItem,
  ListItemText,
} from "@mui/material";
import { CloseOutlined } from "@ant-design/icons";
import "../css/AddPassengerDialog.css";
import { MobileDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { DATE_FORMAT, DOCUMENT_TYPE, DocumentTypeEnum } from "../../utils/constants";
import DateLocalizationProvider from "./DateLocalizationProvider";

const AddPassengerDialog = ({
  open,
  onClose,
  passengersAdded,
  setPassengersAdded,
}) => {
  const [passengerFullName, setPassengerFullName] = useState("");
  const [passengerDOB, setPassengerDOB] = useState(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState("");
  const [passengerDocumentNumber, setPassengerDocumentNumber] = useState("");
  const [passengerDocumentIssueDate, setPassengerDocumentIssueDate] =
    useState(null);
  const [passengerDocumentExpiryDate, setPassengerDocumentExpiryDate] =
    useState(null);
  const [documentNumberError, setDocumentNumberError] = useState("");

  const handleAddPassenger = () => {
    const passengerData = {
      fullName: passengerFullName,
      dateOfBirth: dayjs(passengerDOB).format("YYYY-MM-DD"),
      documentType: selectedDocumentType,
      documentNumber: passengerDocumentNumber,
      documentIssueDate: dayjs(passengerDocumentIssueDate).format("YYYY-MM-DD"),
      documentExpiryDate: dayjs(passengerDocumentExpiryDate).format(
        "YYYY-MM-DD"
      ),
    };
    setPassengersAdded([...passengersAdded, passengerData]);
    clearData();
    onClose();
  };

  const handleFullNameChange = (event) => {
    setPassengerFullName(event.target.value);
  };

  const handlePassengerDOBChange = (newValue) => {
    setPassengerDOB(newValue);
  };

  const handleDocumentNumberChange = (event) => {
    const value = event.target.value;
    if (selectedDocumentType === DocumentTypeEnum.NIB_CARD) {
      // Only allow numeric input and limit to 8 digits
      if (/^\d*$/.test(value) && value.length <= 8) {
        setPassengerDocumentNumber(value);
        if (value.length !== 8) {
          setDocumentNumberError("NIB Card number must be exactly 8 digits");
        } else {
          setDocumentNumberError("");
        }
      }
    } else {
      setPassengerDocumentNumber(value);
      setDocumentNumberError("");
    }
  };

  const handleDocumentIssueDateChange = (newValue) => {
    setPassengerDocumentIssueDate(newValue);
  };

  const handleDocumentExpiryDateChange = (newValue) => {
    setPassengerDocumentExpiryDate(newValue);
  };

  const handleDocumentTypeToggle = (event) => {
    setSelectedDocumentType(event.target.value);
    setPassengerDocumentNumber("");
    setDocumentNumberError("");
  };

  const clearData = () => {
    setPassengerFullName("");
    setPassengerDOB(null);
    setSelectedDocumentType("");
    setPassengerDocumentNumber("");
    setPassengerDocumentIssueDate(null);
    setPassengerDocumentExpiryDate(null);
    setDocumentNumberError("");
  };

  const isValidForAdd = () => {
    return (
      passengerFullName === "" ||
      passengerDOB === null ||
      selectedDocumentType === "" ||
      passengerDocumentNumber === "" ||
      passengerDocumentIssueDate === null ||
      passengerDocumentExpiryDate === null
    );
  };

  return (
    <Dialog
      open={open ?? false}
      onClose={onClose}
      PaperProps={{
        className: "add-passenger-dialog-container",
      }}
    >
      <DialogContent>
        <CloseOutlined
          className="add-passenger-dialog-close"
          onClick={onClose}
        />
        <div className="add-passenger-dialog-content">
          <div className="add-passenger-dialog-title">Add Passenger</div>
          <div className="add-passenger-input-container">
            <label>Passenger Full Name</label>
            <input
              className="add-passenger-input"
              name="profile-registration-number"
              value={passengerFullName}
              onChange={handleFullNameChange}
            />
          </div>
          <DateLocalizationProvider>
            <div className="add-passenger-input-container">
              <label>Passenger Date of Birth</label>
              <MobileDatePicker
                id="add-passenger-dob-datepicker"
                className="add-passenger-datepicker"
                format={DATE_FORMAT}
                maxDate={dayjs()}
                value={passengerDOB}
                onChange={handlePassengerDOBChange}
              />
            </div>
          </DateLocalizationProvider>
          <div className="add-passenger-input-container">
            <label>Document Type</label>
            <Select
              className="add-passenger-select-container"
              value={selectedDocumentType}
              onChange={handleDocumentTypeToggle}
              renderValue={(selected) =>
                DOCUMENT_TYPE.find((type) => type.value === selected)?.name
              }
            >
              {DOCUMENT_TYPE.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <ListItemText primary={type.name} />
                </MenuItem>
              ))}
            </Select>
          </div>
          <div className="add-passenger-input-container">
            <label>Document Number</label>
            <input
              className="add-passenger-input"
              name="profile-registration-number"
              value={passengerDocumentNumber}
              placeholder={selectedDocumentType === DocumentTypeEnum.NIB_CARD ? "Enter 8-digit NIB Card Number" : "Enter Document Number"}
              onChange={handleDocumentNumberChange}
            />
            {documentNumberError && (
              <div className="error-message">{documentNumberError}</div>
            )}
          </div>
          <DateLocalizationProvider>
            <div className="add-passenger-input-container">
              <label>Document Issue Date</label>
              <MobileDatePicker
                id="add-passenger-document-issue-datepicker"
                className="add-passenger-datepicker"
                format={DATE_FORMAT}
                maxDate={dayjs()}
                value={passengerDocumentIssueDate}
                onChange={handleDocumentIssueDateChange}
              />
            </div>
          </DateLocalizationProvider>
          <DateLocalizationProvider>
            <div className="add-passenger-input-container">
              <label>Document Expiry Date</label>
              <MobileDatePicker
                id="add-passenger-document-expiry-datepicker"
                className="add-passenger-datepicker"
                format={DATE_FORMAT}
                minDate={dayjs()}
                value={passengerDocumentExpiryDate}
                onChange={handleDocumentExpiryDateChange}
              />
            </div>
          </DateLocalizationProvider>
          <Button
            variant="contained"
            onClick={handleAddPassenger}
            disabled={isValidForAdd()}
          >
            Add
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddPassengerDialog;
